/**
 * Modal for moving an appointment to a new slot-backed start time while preserving duration.
 */

import React, { useMemo, useEffect } from 'react';
import { Modal, Form, Button, Space, Typography, Alert, App, Flex, Row, Col, theme } from 'antd';
import {
  ApartmentOutlined,
  ArrowRightOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  UserOutlined,
  SyncOutlined,
  ScheduleOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  useUpdateAppointmentMutation,
  type UpdateAppointmentRequest
} from '@/api/mcp-appointments';
import { parseISO } from 'date-fns';
import dayjs, { type Dayjs } from 'dayjs';
import { type Appointment } from '@/api/mcp-appointments';
import {
  formatAppointmentDateTime,
  formatAppointmentTimeRange
} from '../calendar/calendarUtils';
import { getAppointmentClientDisplayLabel } from '../appointmentClientDisplay';
import { useClientCustomerDisplayMap } from '@/hooks/useClientCustomerDisplayMap';
import { getApiErrorMessage } from '@/utils/apiError';
import { ConflictAlerts } from '../create-appointment/components/ConflictAlerts';
import {
  AppointmentSlotDateTimeField,
  isAppointmentSlotSubmitDisabled,
  useAppointmentSlotSelection,
  useRescheduleSlotSetup,
  useServiceAppointmentSlotSelection
} from '../slot-scheduling';
import { serviceNeedsPrimaryResource } from '@/utils/serviceBooking';
import { AppointmentDetailRow } from './AppointmentDetailRow';
import { AppointmentDetailsBox } from './AppointmentDetailsBox';
import { ModalTitle } from '@/components/ui/ModalTitle';
import { AppointmentDetailSectionLabel } from './AppointmentDetailSectionLabel';
import { formModalWidth } from '@/components/ui/formModalStyles';

const { Text } = Typography;

/** Props for the manual reschedule modal. */
interface ManualRescheduleDialogProps {
  visible: boolean;
  onCancel: () => void;
  appointment: Appointment | null;
}

const ManualRescheduleDialog: React.FC<ManualRescheduleDialogProps> = ({
  visible,
  onCancel,
  appointment
}) => {
  const { message } = App.useApp();
  const { t, i18n } = useTranslation('manage');
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const updateAppointmentMutation = useUpdateAppointmentMutation();
  const { userDisplayById } = useClientCustomerDisplayMap(visible);
  const modalBodyRef = React.useRef<HTMLDivElement>(null);
  const initializedAppointmentKeyRef = React.useRef<string | null>(null);

  const {
    isServiceBooking,
    service,
    resourceDurationMinutes,
    durationMinutes,
    primaryResourceId
  } = useRescheduleSlotSetup(appointment, visible);

  const formattedDate = useMemo(() => {
    if (!appointment) return '';
    try {
      return formatAppointmentDateTime(appointment.startTime, i18n.language);
    } catch {
      return appointment.startTime;
    }
  }, [appointment, i18n.language]);

  const originalStartTime = useMemo(() => {
    if (!appointment) return null;
    try {
      return dayjs(parseISO(appointment.startTime));
    } catch {
      return null;
    }
  }, [appointment]);

  const rescheduleDialogKey = appointment
    ? `${appointment.id}:${appointment.startTime}`
    : null;

  const treatAsValidSlot = useMemo(() => {
    if (!originalStartTime || !originalStartTime.isValid() || durationMinutes <= 0) {
      return null;
    }
    return {
      start: originalStartTime,
      end: originalStartTime.add(durationMinutes, 'minute')
    };
  }, [originalStartTime, durationMinutes]);

  const resourceSlotSelection = useAppointmentSlotSelection({
    form,
    resourceId: appointment?.resourceId,
    duration: resourceDurationMinutes,
    visible: visible && !isServiceBooking,
    initialCommitted: true,
    treatAsValidSlot
  });

  const serviceSlotSelection = useServiceAppointmentSlotSelection({
    form,
    service: isServiceBooking ? (service ?? null) : null,
    visible: visible && isServiceBooking && !!service,
    initialCommitted: true,
    treatAsValidSlot,
    excludeAppointmentId: appointment?.id,
    lockedPrimaryResourceId: primaryResourceId
  });

  const slotSelection = isServiceBooking ? serviceSlotSelection : resourceSlotSelection;

  const {
    initializeForOpen,
    resetForClose,
    displayedSlotUnavailable,
    isCheckingSelectedMonthSlots,
    hasCommittedStartTime,
    isSelectedSlotValid,
    truncated
  } = slotSelection;

  const isSubmitDisabled = isAppointmentSlotSubmitDisabled({
    hasCommittedStartTime,
    slotUnavailable: displayedSlotUnavailable,
    truncated,
    isCheckingSelectedMonthSlots
  });

  useEffect(() => {
    if (visible && appointment && originalStartTime && rescheduleDialogKey) {
      if (initializedAppointmentKeyRef.current === rescheduleDialogKey) {
        return;
      }
      initializedAppointmentKeyRef.current = rescheduleDialogKey;
      initializeForOpen({
        initialStartTime: originalStartTime,
        committed: true
      });
      form.setFieldsValue({ startTime: originalStartTime });
    } else if (!visible) {
      initializedAppointmentKeyRef.current = null;
      resetForClose();
    }
  }, [visible, appointment, originalStartTime, rescheduleDialogKey, form, initializeForOpen, resetForClose]);

  const handleSubmit = async (values: { startTime: Dayjs }) => {
    if (!appointment) return;

    if (isCheckingSelectedMonthSlots) {
      message.error(t('appointmentComponents.manualReschedule.stillChecking'));
      return;
    }

    if (!hasCommittedStartTime) {
      message.error(t('appointmentComponents.manualReschedule.selectValidTime'));
      return;
    }

    if (truncated && !isSelectedSlotValid) {
      message.error(t('appointmentComponents.manualReschedule.slotNotAvailable'));
      return;
    }

    if (displayedSlotUnavailable?.hasViolation || !isSelectedSlotValid) {
      message.error(t('appointmentComponents.manualReschedule.slotNotAvailable'));
      return;
    }

    const startTimeValue = values.startTime;
    const startDayjs = dayjs.isDayjs(startTimeValue) ? startTimeValue : dayjs(startTimeValue);
    if (!startDayjs.isValid()) {
      message.error(t('appointmentComponents.manualReschedule.selectValidTime'));
      return;
    }

    const endTime = startDayjs.add(durationMinutes, 'minute');

    try {
      const updateBody: UpdateAppointmentRequest = {
        startTime: startDayjs.toISOString(),
        endTime: endTime.toISOString()
      };

      await updateAppointmentMutation.mutateAsync({
        appointmentId: appointment.id,
        body: updateBody
      });

      message.success(t('appointmentComponents.manualReschedule.success'));
      form.resetFields();
      onCancel();
    } catch (error) {
      message.error(getApiErrorMessage(error, t('appointmentComponents.manualReschedule.error')));
      console.error('Failed to reschedule appointment:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  if (!appointment) return null;

  const serviceName = appointment.service?.name ?? service?.name;
  const allocations = appointment.allocatedResources ?? [];
  const showPrimaryReadOnly =
    isServiceBooking && service && serviceNeedsPrimaryResource(service);
  const primaryAllocation = allocations.find((r) => r.isPrimary) ?? allocations[0];
  const conflictResourceId = isServiceBooking ? primaryResourceId : appointment.resourceId;
  const clientLabel = getAppointmentClientDisplayLabel(
    appointment,
    t('appointmentComponents.details.anonymous'),
    userDisplayById
  );

  return (
    <Modal
      title={
        <ModalTitle
          icon={<SyncOutlined />}
          title={t('appointmentComponents.manualReschedule.title')}
          subtitle={`${clientLabel} · ${formattedDate}`}
        />
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={formModalWidth(980)}
      destroyOnHidden
      styles={{
        body: {
          paddingTop: token.paddingSM
        }
      }}
    >
      <div ref={modalBodyRef}>
        <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
          <Row gutter={[token.marginLG, token.marginMD]} style={{ alignItems: 'stretch' }}>
            <Col xs={24} lg={10}>
              <Flex vertical gap={token.marginMD}>
                <div>
                  <AppointmentDetailSectionLabel>
                    {t('appointmentComponents.manualReschedule.appointmentId', { id: appointment.id.slice(0, 8) })}
                  </AppointmentDetailSectionLabel>
                  <AppointmentDetailsBox>
                    {isServiceBooking && serviceName ? (
                      <AppointmentDetailRow
                        icon={<ScheduleOutlined />}
                        label={t('appointmentComponents.manualReschedule.service')}
                      >
                        <Text strong>{serviceName}</Text>
                      </AppointmentDetailRow>
                    ) : null}
                    <AppointmentDetailRow
                      icon={<UserOutlined />}
                      label={t('appointmentComponents.manualReschedule.client')}
                    >
                      <Text strong>{clientLabel}</Text>
                    </AppointmentDetailRow>
                    <AppointmentDetailRow
                      icon={<ClockCircleOutlined />}
                      label={t('appointmentComponents.manualReschedule.currentTime')}
                    >
                      <Text>{formatAppointmentTimeRange(appointment.startTime, appointment.endTime, i18n.language)}</Text>
                    </AppointmentDetailRow>
                    <AppointmentDetailRow
                      icon={<FieldTimeOutlined />}
                      label={t('appointmentComponents.manualReschedule.duration')}
                    >
                      <Text>
                        {isServiceBooking
                          ? t('appointmentComponents.manualReschedule.durationLocked', { minutes: durationMinutes })
                          : t('appointmentComponents.manualReschedule.durationMinutes', { minutes: durationMinutes })}
                      </Text>
                    </AppointmentDetailRow>
                    {showPrimaryReadOnly && primaryAllocation ? (
                      <AppointmentDetailRow
                        icon={<ApartmentOutlined />}
                        label={t('appointmentComponents.manualReschedule.primaryResource')}
                      >
                        <Text>{primaryAllocation.name}</Text>
                      </AppointmentDetailRow>
                    ) : null}
                    {isServiceBooking && allocations.length > 0 ? (
                      <AppointmentDetailRow
                        icon={<TeamOutlined />}
                        label={t('appointmentComponents.manualReschedule.assignedResources')}
                      >
                        <Text type="secondary">{allocations.map((r) => r.name).join(', ')}</Text>
                      </AppointmentDetailRow>
                    ) : null}
                  </AppointmentDetailsBox>
                </div>

                <Alert
                  title={t('appointmentComponents.manualReschedule.alertTitle')}
                  description={
                    isServiceBooking
                      ? t('appointmentComponents.manualReschedule.alertDescriptionService')
                      : t('appointmentComponents.manualReschedule.alertDescription')
                  }
                  type="info"
                  showIcon
                />
              </Flex>
            </Col>

            <Col xs={24} lg={14} style={{ display: 'flex', minWidth: 0 }}>
              <Flex vertical gap={token.marginMD} style={{ width: '100%', minWidth: 0 }}>
                <ConflictAlerts
                  slotUnavailable={displayedSlotUnavailable}
                  isCheckingSlots={isCheckingSelectedMonthSlots}
                  truncated={truncated}
                  resourceId={conflictResourceId}
                  hasCommittedStartTime={hasCommittedStartTime}
                  onDismissSlotUnavailable={() => slotSelection.setDismissedSlotUnavailable(true)}
                />

                <AppointmentSlotDateTimeField
                  form={form}
                  dialogKey={rescheduleDialogKey ?? appointment.id}
                  resourceId={conflictResourceId}
                  duration={durationMinutes}
                  visible={visible}
                  initialCommitted
                  treatAsValidSlot={treatAsValidSlot}
                  selection={slotSelection}
                  showAlerts={false}
                  displayMode="inline"
                  label={
                    <Space size="small">
                      <span>{t('appointmentComponents.manualReschedule.newStartLabel')}</span>
                      <Text type="danger">*</Text>
                    </Space>
                  }
                  size="middle"
                />

                <Form.Item shouldUpdate={(prev, cur) => prev.startTime !== cur.startTime} noStyle>
                  {({ getFieldValue }) => {
                    const pickedStart = getFieldValue('startTime');
                    if (!pickedStart || !appointment) {
                      return null;
                    }
                    const calculatedEndTime = pickedStart.add(durationMinutes, 'minute');
                    const isTimeChanged = !originalStartTime || !pickedStart.isSame(originalStartTime, 'minute');
                    return (
                      <AppointmentDetailsBox filled>
                        {isTimeChanged ? (
                          <>
                            <AppointmentDetailRow
                              icon={<ClockCircleOutlined />}
                              label={t('appointmentComponents.manualReschedule.currentTime')}
                            >
                              <Text type="secondary" delete>
                                {formatAppointmentTimeRange(appointment.startTime, appointment.endTime, i18n.language)}
                              </Text>
                            </AppointmentDetailRow>
                            <AppointmentDetailRow
                              icon={<ArrowRightOutlined />}
                              label={t('appointmentComponents.manualReschedule.newTime')}
                            >
                              <Text strong style={{ color: token.colorPrimary }}>
                                {formatAppointmentTimeRange(
                                  pickedStart.toISOString(),
                                  calculatedEndTime.toISOString(),
                                  i18n.language
                                )}
                              </Text>
                            </AppointmentDetailRow>
                          </>
                        ) : (
                          <AppointmentDetailRow
                            icon={<FieldTimeOutlined />}
                            label={t('appointmentComponents.manualReschedule.endTime')}
                          >
                            <Text strong>
                              {formatAppointmentDateTime(calculatedEndTime.toISOString(), i18n.language)}
                            </Text>
                          </AppointmentDetailRow>
                        )}
                      </AppointmentDetailsBox>
                    );
                  }}
                </Form.Item>

                <Flex
                  gap={token.marginXS}
                  justify="flex-end"
                  wrap="wrap"
                  style={{
                    marginTop: 'auto',
                    paddingTop: token.paddingMD,
                    borderTop: `1px solid ${token.colorBorderSecondary}`
                  }}
                >
                  <Button onClick={handleCancel}>
                    {t('appointmentComponents.create.actions.cancel')}
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={updateAppointmentMutation.isPending || isCheckingSelectedMonthSlots}
                    disabled={isSubmitDisabled}
                    icon={!updateAppointmentMutation.isPending ? <SyncOutlined /> : undefined}
                  >
                    {updateAppointmentMutation.isPending
                      ? t('appointmentComponents.manualReschedule.rescheduling')
                      : t('appointmentComponents.manualReschedule.reschedule')}
                  </Button>
                </Flex>
              </Flex>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default ManualRescheduleDialog;
